import {FirebaseMessaging} from '@capacitor-firebase/messaging';
import {LocalNotifications} from '@capacitor/local-notifications';
import {Capacitor} from '@capacitor/core';
import {App} from '@capacitor/app';
import {ref, Ref} from 'vue';
import {AppNotification, setPendingNotification} from '@/stores/notificationStore';
import {apiFetch} from "@/services/ApiClient.ts";

export const useNotifications = () => {

    const parsePayload = (notification: any): AppNotification => {
        const data = notification.data || {};

        return {
            notificationId: data.notificationId || notification.notificationId,
            type: data.type || notification.type,
            title: notification.title || '',
            body: notification.body || '',
            subject: data.subject || notification.title, // fallback to FCM title
            message: data.message || notification.body, // fallback to FCM body
            actionId: data.actionId,
            biometricsRequired: data.biometricsRequired === true || data.biometricsRequired === 'true',
            mustBeInStore: data.mustBeInStore === true || data.mustBeInStore === 'true',
            timeoutSec: data.timeoutSec ? Number(data.timeoutSec) : undefined,
            expiresAt: data.expiresAt || notification.expiresAt,
        };
    };

    const processPayload = async (payload: AppNotification, isAuthenticated: Ref<boolean>, isAppReady: Ref<boolean>) => {
        await setPendingNotification(payload);

        const {isActive} = await App.getState();
        if (isActive && isAppReady.value) {
            const {processPendingNotification} = await import('@/services/NotificationHandler');
            await processPendingNotification(isAuthenticated.value);
        }
    };

    const fcmToken = ref<string | null>(null);

    const postDeviceToken = async (token: string) => {
        try {
            await apiFetch('/updateDeviceToken', {
                method: 'POST',
                body: JSON.stringify({deviceToken: token})
            });
        } catch (error) {
            console.error('updateDeviceToken failed', error);
        }
    };

    const setupNotifications = async (isAuthenticated: Ref<boolean>, isAppReady: Ref<boolean>) => {
        if (Capacitor.getPlatform() === 'web') return;

        if (Capacitor.getPlatform() === 'android') {
            await LocalNotifications.createChannel({
                id: 'actions_channel', // must match channel id in manifest
                name: 'Actions',
                importance: 5, // highest
                visibility: 1,
                vibration: true,
            });
        }

        const perm = await FirebaseMessaging.requestPermissions();
        if (perm.receive !== 'granted') return;

        // foreground: show in tray via local notification
        await FirebaseMessaging.addListener('notificationReceived', async (event) => {
            await LocalNotifications.schedule({
                notifications: [{
                    title: event.notification.title || '',
                    body: event.notification.body || '',
                    id: Math.floor(Math.random() * 1000000), // safe java int system id for tray management
                    extra: event.notification.data,
                    channelId: 'actions_channel',
                }]
            });
        });

        // background/killed tap
        await FirebaseMessaging.addListener('notificationActionPerformed', async (event) => {
            const payload = parsePayload(event.notification);
            await processPayload(payload, isAuthenticated, isAppReady);
        });

        // foreground tap (via local notification)
        await LocalNotifications.addListener('localNotificationActionPerformed', async (event) => {
            // local notification stores FCM data in extra, shaped like notification.data
            // wrap it so parsePayload sees the same structure as the FCM path
            const payload = parsePayload({
                data: event.notification.extra || {},
                title: event.notification.title,
                body: event.notification.body,
            });
            await processPayload(payload, isAuthenticated, isAppReady);
        });

        const {token} = await FirebaseMessaging.getToken();
        fcmToken.value = token;

        if (isAuthenticated.value) {
            await postDeviceToken(token);
        }

        await FirebaseMessaging.addListener('tokenReceived', async (event) => {
            fcmToken.value = event.token;
            if (isAuthenticated.value) {
                await postDeviceToken(event.token);
            }
        });
    };

    const sendTokenIfReady = async () => {
        if (fcmToken.value) {
            await postDeviceToken(fcmToken.value);
        }
    };

    return {setupNotifications, sendTokenIfReady};
};